import dayjs from 'dayjs';
import { FtcAnswer } from 'core/sites/types';

export interface FormState {
    timeOn: dayjs.Dayjs | null;
    timeOff: dayjs.Dayjs | null;
    internalPatrol: FtcAnswer | null;
    externalPatrol: FtcAnswer | null;
    idsFobHeld: FtcAnswer | null;
    idsFobWorks: FtcAnswer | null;
    storeKeysHeld: FtcAnswer | null;
    storeKeysWork: FtcAnswer | null;
    shutterKeyHeld: FtcAnswer | null;
    shutterKeyWorks: FtcAnswer | null;
    responseSetIdsOnDeparture: FtcAnswer | null;
    remoteEngSetOnDeparture: FtcAnswer | null;
    khcReference: string;
    comments: string;
}

// The Yes/No/N/A fields, i.e. every key whose value is a nullable answer.
export type AnswerField = {
    [K in keyof FormState]: FormState[K] extends FtcAnswer | null ? K : never
}[keyof FormState];

// Everything the backend needs before the update report can be sent; comments
// are the only optional field.
export const REQUIRED_FIELDS: (keyof FormState)[] = [
    'timeOn',
    'timeOff',
    'internalPatrol',
    'externalPatrol',
    'idsFobHeld',
    'idsFobWorks',
    'storeKeysHeld',
    'storeKeysWork',
    'shutterKeyHeld',
    'shutterKeyWorks',
    'responseSetIdsOnDeparture',
    'remoteEngSetOnDeparture',
    'khcReference',
];

export const EMPTY_FORM: FormState = {
    timeOn: null,
    timeOff: null,
    internalPatrol: null,
    externalPatrol: null,
    idsFobHeld: null,
    idsFobWorks: null,
    storeKeysHeld: null,
    storeKeysWork: null,
    shutterKeyHeld: null,
    shutterKeyWorks: null,
    responseSetIdsOnDeparture: null,
    remoteEngSetOnDeparture: null,
    khcReference: '',
    comments: '',
};

// An internal patrol means the officer got inside with working keys, so
// answering it Yes answers these five as well. Changing it back to No leaves
// them blank for the officer to fill in.
const INTERNAL_PATROL_ANSWERS: AnswerField[] = [
    'externalPatrol',
    'storeKeysHeld',
    'storeKeysWork',
    'shutterKeyHeld',
    'shutterKeyWorks',
];

// Whether the fob, key or remote works defaults to N/A when none was held, or
// none applies. The officer can still overrule it.
const NOT_APPLICABLE_WHEN: Partial<Record<AnswerField, AnswerField>> = {
    idsFobWorks: 'idsFobHeld',
    storeKeysWork: 'storeKeysHeld',
    shutterKeyWorks: 'shutterKeyHeld',
};

// At most one of the two set the alarm on departure, so a Yes answers the other
// N/A. A No is left to stand on its own: the alarm may have been set by neither.
const IDS_DEPARTURE_PAIR: Partial<Record<AnswerField, AnswerField>> = {
    responseSetIdsOnDeparture: 'remoteEngSetOnDeparture',
    remoteEngSetOnDeparture: 'responseSetIdsOnDeparture',
};

/**
 * Whether the field defaults to N/A given the rest of the form: a "works"
 * answer is presumed N/A while the matching key or remote is not held.
 */
export const isNotApplicable = (form: FormState, field: keyof FormState): boolean => {
    const heldField = NOT_APPLICABLE_WHEN[field as AnswerField];

    return heldField !== undefined && (form[heldField] === 'no' || form[heldField] === 'na');
};

/**
 * Fill in the N/A defaults for any "works" field left blank while its key or
 * remote is not held, so a stored entry reads the same as a freshly answered one.
 */
export const applyDefaults = (form: FormState): FormState => {
    const next: FormState = { ...form };

    (Object.keys(NOT_APPLICABLE_WHEN) as AnswerField[]).forEach((worksField) => {
        if (next[worksField] === null && isNotApplicable(next, worksField)) {
            next[worksField] = 'na';
        }
    });

    return next;
};

/**
 * Whether the field still needs an answer before the report can be sent.
 *
 * N/A is a full answer on every question.
 */
export const isFieldMissing = (form: FormState, field: keyof FormState): boolean => {
    if (field === 'khcReference') {
        return form.khcReference.trim() === '';
    }

    return form[field] === null;
};

/**
 * Answer a Yes/No/N/A field, applying the answers that follow from it.
 */
export const applyFieldRules = (
    form: FormState,
    field: AnswerField,
    value: FtcAnswer | null,
): FormState => {
    const next: FormState = { ...form, [field]: value };

    if (field === 'internalPatrol') {
        INTERNAL_PATROL_ANSWERS.forEach((answered) => {
            next[answered] = value === 'yes' ? 'yes' : null;
        });
    }

    // Answering No or N/A, or clearing the field back to blank, leaves the
    // other one alone: both can be No when neither set the alarm.
    const pairedField = IDS_DEPARTURE_PAIR[field];
    if (pairedField !== undefined && value === 'yes') {
        next[pairedField] = 'na';
    }

    // Answering a held field resets its works field: N/A when the key is not
    // held (the officer can still change it), and asked again when a key is
    // held after all.
    (Object.entries(NOT_APPLICABLE_WHEN) as [AnswerField, AnswerField][]).forEach(([worksField, heldField]) => {
        if (field !== heldField) {
            return;
        }
        if (isNotApplicable(next, worksField)) {
            next[worksField] = 'na';
        } else if (next[worksField] === 'na') {
            next[worksField] = null;
        }
    });

    return next;
};
